import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Trash2, X, ChevronLeft, ChevronRight, Edit, Search } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { useSignedUrls } from '../../hooks/useSignedUrl';
import { SignedImage } from '../common/SignedFile';
import { TrailerPhotoWizard } from './TrailerPhotoWizard';

interface TrailerPhoto {
  id: string;
  trailer_number: string;
  person: string | null;
  out_in: 'OUT' | 'IN' | null;
  driver_name: string | null;
  company: string | null;
  inspection_date: string | null;
  yard_letter: string | null;
  pictures_4_sides: string[] | null;
  tires: string[] | null;
  visible_damage: string[] | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

export function TrailerPhotosView() {
  const [trailerPhotos, setTrailerPhotos] = useState<TrailerPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [editingInspection, setEditingInspection] = useState<TrailerPhoto | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const { showToast } = useToast();

  // Photos live in a private bucket. Sign the first photo of every cell in ONE
  // request (the table is unpaginated), and the open lightbox's photos in another.
  const thumbStored = trailerPhotos.flatMap(row => [
    row.yard_letter,
    row.pictures_4_sides?.[0],
    row.tires?.[0],
    row.visible_damage?.[0]
  ].filter((v): v is string => !!v));
  const { urls: thumbUrls, loading: thumbsLoading } = useSignedUrls(thumbStored);
  const { urls: previewUrls, loading: previewLoading } = useSignedUrls(previewImage ? previewImages : []);

  useEffect(() => {
    loadTrailerPhotos();
  }, []);

  const loadTrailerPhotos = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('trailer_photos')
        .select('*')
        .order('inspection_date', { ascending: false });

      if (error) throw error;
      setTrailerPhotos(data || []);
    } catch (error: any) {
      showToast('error', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this trailer photo record?')) return;

    try {
      const { error } = await supabase
        .from('trailer_photos')
        .delete()
        .eq('id', id);

      if (error) throw error;
      showToast('success', 'Record deleted');
      loadTrailerPhotos();
    } catch (error: any) {
      showToast('error', error.message);
    }
  };

  const renderPhotoCell = (photos: string[] | null) => {
    if (!photos || photos.length === 0) return <span className="text-slate-400">-</span>;
    
    return (
      <div className="flex items-center justify-center gap-2">
        <div 
          className="relative cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => {
            setPreviewImages(photos);
            setCurrentImageIndex(0);
            setPreviewImage(photos[0]);
          }}
        >
          {thumbsLoading ? (
            <div className="w-10 h-10 rounded border border-slate-200 bg-slate-100 animate-pulse" aria-busy="true" />
          ) : thumbUrls.get(photos[0].trim()) ? (
            <SignedImage
              stored={photos[0]}
              alt="Preview"
              className="w-10 h-10 object-cover rounded border border-slate-200"
            />
          ) : (
            <div
              className="w-10 h-10 rounded border border-slate-200 bg-slate-100 flex items-center justify-center text-[9px] text-slate-400 text-center leading-tight"
              title="This image could not be loaded"
            >
              N/A
            </div>
          )}
          {photos.length > 1 && (
            <div className="absolute -top-1 -right-1 bg-slate-700 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
              +{photos.length - 1}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Filter trailer photos by search query (trailer number)
  const filteredTrailerPhotos = trailerPhotos.filter(photo =>
    photo.trailer_number.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-3 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Trailer Photos</h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">Manage trailer inspection photos and documentation</p>
        </div>
        <button
          onClick={() => setShowWizard(true)}
          className="flex items-center justify-center gap-2 px-4 py-3 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium w-full sm:w-auto"
        >
          <Plus className="w-5 h-5" />
          <span>Add Trailer Photo</span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search by trailer number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto -mx-3 sm:mx-0">
          <table className="w-full min-w-[1000px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">Trailer #</th>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">Person</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">OUT/IN</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Driver Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Company</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Yard</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">4 Sides</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Tires</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Damage</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Last Updated</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredTrailerPhotos.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-4 py-8 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Search className="w-12 h-12 text-slate-300" />
                      <p className="text-slate-500 font-medium">
                        {searchQuery ? 'No trailers match your search' : 'No trailer photo records found'}
                      </p>
                      {searchQuery ? (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          Clear search
                        </button>
                      ) : (
                        <span className="text-sm text-slate-400">Click "Add Trailer Photo" to create one</span>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredTrailerPhotos.map((photo) => (
                  <tr key={photo.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-medium text-slate-900">{photo.trailer_number}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{photo.person || '-'}</td>
                    <td className="px-4 py-3">
                      {photo.out_in && (
                        <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                          photo.out_in === 'OUT' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {photo.out_in}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{photo.driver_name || '-'}</td>
                    <td className="px-4 py-3">
                      {photo.company && (
                        <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                          photo.company === 'APOLLO' ? 'bg-blue-100 text-blue-700' :
                          photo.company === 'TESLA' ? 'bg-purple-100 text-purple-700' :
                          photo.company === 'TMARK' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {photo.company}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {photo.inspection_date ? new Date(photo.inspection_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {renderPhotoCell(photo.yard_letter ? [photo.yard_letter] : null)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {renderPhotoCell(photo.pictures_4_sides)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {renderPhotoCell(photo.tires)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {renderPhotoCell(photo.visible_damage)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {new Date(photo.updated_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setEditingInspection(photo);
                            setShowWizard(true);
                          }}
                          className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Edit inspection"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(photo.id)}
                          className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          title="Delete inspection"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Wizard Modal */}
      {showWizard && (
        <TrailerPhotoWizard
          existingData={editingInspection}
          onClose={() => {
            setShowWizard(false);
            setEditingInspection(null);
          }}
          onSuccess={() => {
            loadTrailerPhotos();
            setShowWizard(false);
            setEditingInspection(null);
          }}
        />
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <X className="w-8 h-8" />
            </button>
            
            {previewLoading ? (
              <div className="w-full h-64 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              </div>
            ) : previewUrls.get((previewImages[currentImageIndex] || '').trim()) ? (
              <SignedImage
                key={currentImageIndex}
                stored={previewImages[currentImageIndex]}
                alt="Preview"
                className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
              />
            ) : (
              <div className="w-full h-64 flex items-center justify-center text-white text-sm">
                This image could not be loaded
              </div>
            )}
            
            {previewImages.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : previewImages.length - 1))}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-75 hover:bg-opacity-100 rounded-full p-2"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={() => setCurrentImageIndex((prev) => (prev < previewImages.length - 1 ? prev + 1 : 0))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-75 hover:bg-opacity-100 rounded-full p-2"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                  {currentImageIndex + 1} / {previewImages.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
